/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import CustomModal from "../../../Core/Components/Modal/CustomModal";
import { TrashSecondaryIcon } from "../../../Common/Svgs/TrashIcon";
import CustomRoundedImage from "../../../Core/Components/CustomRoundedImage";
import LocationsModuleIcon from "../../../Common/Svgs/LocationsModuleIcon";
import CloseIcon from "../../../Common/Svgs/CloseIcon";
import Button from "../../../Core/Components/CustomButton";
import {
  checkHubStationDelete,
  deleteHubStation,
  getAllLocationsV3,
  getLocationsV3,
} from "../../../Redux/APIs/locationsAPI";
import { updateLocations } from "../../../Redux/Slices/locations.slice";

const LOCAL_DEFAULT_LIMIT = 10;

const DeleteHubStationModal = ({
  visible,
  onCancel,
  currentPage,
  setCurrentPage,
}) => {
  const dispatch = useDispatch();

  const { deleteHubStationModal } = useSelector((state) => state.locations);

  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const onDelete = async () => {
    await dispatch(
      deleteHubStation({ locationId: deleteHubStationModal?.hub?.id }),
    );

    const response = await dispatch(
      getLocationsV3({
        offset: ((currentPage || 1) - 1) * LOCAL_DEFAULT_LIMIT,
        limit: LOCAL_DEFAULT_LIMIT,
      }),
    );

    // If no records exist for the current page, move to the previous page
    if (!response.payload.result.length && currentPage > 1) {
      const previousPage = currentPage - 1;
      setCurrentPage(previousPage);

      await dispatch(
        getLocationsV3({
          offset: (previousPage - 1) * LOCAL_DEFAULT_LIMIT,
          limit: LOCAL_DEFAULT_LIMIT,
        }),
      );
    }

    dispatch(getAllLocationsV3());

    await dispatch(
      updateLocations({
        key: "showToastMessage",
        value: {
          visible: true,
          data: {
            title: "1 Location Deleted",
            description: `${
              deleteHubStationModal?.hub?.name || "1 hub station"
            } was deleted sucessfully.`,
          },
        },
      }),
    );

    onCancel();
  };

  const checkDeleteStatus = useCallback(async () => {
    if (visible) {
      setLoading(true);

      const response = await dispatch(
        checkHubStationDelete({ hubId: deleteHubStationModal?.hub?.id }),
      );

      if (+response.payload.deleteStatus === 2) setDisabled(false);
      setLoading(false);
    }
  }, [dispatch, deleteHubStationModal, visible]);

  useEffect(() => {
    checkDeleteStatus();
  }, [checkDeleteStatus]);

  return (
    <CustomModal visible={visible} onCancel={onCancel} width="536px" centered>
      <div className="w-full p-4 rounded-md">
        <div className="flex items-center justify-between">
          <div className="relative">
            <div className="absolute top-0 right-0">
              <TrashSecondaryIcon className="text-alert-red" />
            </div>
            <CustomRoundedImage
              size="w-12 h-12"
              image={<LocationsModuleIcon className="text-white" />}
              background="bg-primary"
            />
          </div>
          <button type="button" onClick={onCancel}>
            <CloseIcon className="w-6 h-6 stroke-dark-gray" />
          </button>
        </div>
        <div className="px-2 mt-5">
          <p className="text-base font-medium text-alert-red font-poppins">
            Delete hub station
          </p>

          {loading ? (
            <div className="flex items-center gap-x-3">
              <p className="mt-2 text-xs font-normal text-black font-aileron">
                Hold on. Checking if this hub station can be deleted or not.
              </p>

              <Spin
                spinning={loading}
                indicator={<LoadingOutlined color="#ffffff" />}
              />
            </div>
          ) : disabled ? (
            <p className="mt-2 text-xs font-normal text-black font-aileron">
              Cannot delete the hub station as there are items associated with
              it. Please assign a new hub station before deleting the hub
              station.
            </p>
          ) : (
            <div>
              <p className="mt-2 text-xs font-normal text-black font-aileron">
                Are you sure you want to delete this location from the system?
              </p>
              <p className="mt-2 text-xs font-normal text-black font-aileron">
                This action cannot be undone.
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-x-3 mt-5.5">
          <Button colors="cancelButton" size="primary" onClick={onCancel}>
            Cancel
          </Button>

          <Button
            colors="alert"
            size="primary"
            onClick={onDelete}
            modifier={disabled && "disabled"}
            disabled={disabled}
          >
            Delete
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};

export default DeleteHubStationModal;
